// Edit Lanes modal — opens from a Routing Matrix cell (origin hub → destination
// hub). For each of the destination hub's branches, choose how the origin hub
// feeds it — None / H2H via the home hub / Direct — and, per branch, its feed %
// and an effective → revert date range (so multiple branches can be rerouted on
// different schedules in one save).

import { useMemo, useState } from "react";
import { Button, Input, Modal } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { today } from "../../lib/format";
import type { Branch, DirectLane, Hub, LaneRouting } from "../../lib/types";
import { saveLaneDirects } from "./directLanesApi";

type Choice = "none" | "h2h" | "direct";
interface Cfg {
  choice: Choice;
  feedPct: string;
  eff: string;
  revert: string;
}

interface Props {
  accountId: string;
  origin: Hub;
  dest: Hub;
  destBranches: Branch[];
  directLanes: DirectLane[];
  autoTransit: number;
  onClose: () => void;
  onSaved: () => void;
}

export default function LaneEditorModal({ accountId, origin, dest, destBranches, directLanes, autoTransit, onClose, onSaved }: Props) {
  const branchIds = useMemo(() => destBranches.map((b) => b.id), [destBranches]);
  const existing = useMemo(
    () => directLanes.filter((l) => l.originHubId === origin.id && branchIds.includes(l.branchId) && l.active && !l.customerId),
    [directLanes, origin.id, branchIds]
  );

  const [cfg, setCfg] = useState<Record<string, Cfg>>(() => {
    const m: Record<string, Cfg> = {};
    for (const b of destBranches) {
      const l = existing.find((x) => x.branchId === b.id);
      m[b.id] = { choice: l ? l.routing : "none", feedPct: String(l?.feedPct ?? 100), eff: l?.effectiveDate ?? today(), revert: l?.endDate ?? "" };
    }
    return m;
  });
  // "Apply to all" defaults for quickly stamping every fed branch.
  const [bulkEff, setBulkEff] = useState(today());
  const [bulkRevert, setBulkRevert] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const oc = origin.code || origin.name;
  const dc = dest.code || dest.name;
  const fedCount = Object.values(cfg).filter((c) => c.choice !== "none").length;
  const set = (id: string, patch: Partial<Cfg>) => setCfg((m) => ({ ...m, [id]: { ...m[id], ...patch } }));
  const applyDatesToAll = () =>
    setCfg((m) => {
      const n = { ...m };
      for (const b of destBranches) if (n[b.id].choice !== "none") n[b.id] = { ...n[b.id], eff: bulkEff, revert: bulkRevert };
      return n;
    });

  async function save() {
    for (const b of destBranches) {
      const c = cfg[b.id];
      if (c.choice !== "none" && c.revert && c.revert < c.eff) {
        setError(`${b.name}: revert date can't be before the effective date.`);
        return;
      }
    }
    setBusy(true);
    setError(null);
    const lanes: DirectLane[] = destBranches
      .filter((b) => cfg[b.id].choice !== "none")
      .map((b) => {
        const c = cfg[b.id];
        return {
          originHubId: origin.id,
          branchId: b.id,
          routing: c.choice as LaneRouting,
          feedPct: Math.max(1, Math.min(100, Number(c.feedPct) || 100)),
          customerId: null,
          active: true,
          effectiveDate: c.eff || today(),
          endDate: c.revert || null,
        };
      });
    try {
      await saveLaneDirects(accountId, origin.id, branchIds, lanes);
      onSaved();
    } catch (e) {
      setError(getErrorMessage(e, "Failed to save lanes."));
      setBusy(false);
    }
  }

  const OPTS: Array<{ key: Choice; label: string; on: string }> = [
    { key: "none", label: "None", on: "bg-slate-600 text-white" },
    { key: "h2h", label: `H2H via ${dc}`, on: "bg-sky-600 text-white" },
    { key: "direct", label: `Direct from ${oc}`, on: "bg-emerald-600 text-white" },
  ];

  return (
    <Modal title="Edit Lanes" onClose={onClose} wide>
      <p className="-mt-2 mb-3 text-xs text-slate-500">
        <span className="font-medium text-slate-700">{oc} → {dc}</span> · {destBranches.length} branch
        {destBranches.length === 1 ? "" : "es"} · direct transit: ~{autoTransit}d
      </p>
      <p className="mb-3 text-xs text-slate-600">
        Choose how <b>{oc}</b> feeds each {dc} branch. <b>None</b> = stays local to its hub. <b>H2H</b> = {oc} ships to {dc}
        , which re-sorts to the branch. <b>Direct</b> = {oc} ships straight to the branch. Each fed branch has its own
        feed % and effective → revert dates.
      </p>

      {destBranches.length === 0 ? (
        <p className="text-sm text-slate-500">This hub has no active branches.</p>
      ) : (
        <div className="space-y-2">
          {destBranches.map((b) => {
            const c = cfg[b.id];
            const fed = c.choice !== "none";
            return (
              <div key={b.id} className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800">{b.name}</span>
                  <div className="flex overflow-hidden rounded-md border border-slate-300 text-xs">
                    {OPTS.map((o, i) => (
                      <button
                        key={o.key}
                        onClick={() => set(b.id, { choice: o.key })}
                        className={`px-3 py-1.5 font-medium transition ${i > 0 ? "border-l border-slate-300" : ""} ${
                          c.choice === o.key ? o.on : "bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                {fed && (
                  <div className="mt-2 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-2">
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-slate-400">Feed %</span>
                      <Input type="number" min={1} max={100} value={c.feedPct} onChange={(e) => set(b.id, { feedPct: e.target.value })} className="w-20 py-1 text-xs" />
                    </label>
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-slate-400">Effective</span>
                      <Input type="date" value={c.eff} onChange={(e) => set(b.id, { eff: e.target.value || today() })} className="w-36 py-1 text-xs" />
                    </label>
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-slate-400">Revert (optional)</span>
                      <Input type="date" value={c.revert} onChange={(e) => set(b.id, { revert: e.target.value })} className="w-36 py-1 text-xs" />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Apply-to-all convenience for the dates */}
      {fedCount > 1 && (
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs">
          <span className="pb-1.5 font-medium text-slate-500">Set dates for all fed branches:</span>
          <Input type="date" value={bulkEff} onChange={(e) => setBulkEff(e.target.value || today())} className="w-36 py-1 text-xs" />
          <span className="pb-1.5 text-slate-400">→</span>
          <Input type="date" value={bulkRevert} onChange={(e) => setBulkRevert(e.target.value)} className="w-36 py-1 text-xs" />
          <Button variant="ghost" onClick={applyDatesToAll} className="py-1 text-xs">
            Apply to all
          </Button>
        </div>
      )}

      <p className="mt-2 text-[11px] text-slate-400">
        {fedCount === 0 ? `No branches fed by ${oc} — all stay local.` : `${fedCount} branch${fedCount === 1 ? "" : "es"} fed by ${oc}.`}
      </p>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Modal>
  );
}
